import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  MenuItem,
  Grid2 as Grid,
  Button,
  Tabs,
  Tab,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp,
  AttachMoney,
  People,
  Receipt,
  Download,
  DateRange,
  BarChart,
  PieChart,
  Timeline,
  Assessment,
  Refresh,
} from '@mui/icons-material';
import {
  BarChart as RechartsBarChart,
  Bar,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
} from 'recharts';
import reportsService, {
  ReportOverview,
  PaymentOverTimeData,
  EntityPaymentData,
  PaymentMethodData,
  DailyTransactionData,
  ReportPeriod,
} from '../../services/reports.service';
import { useTranslation } from 'react-i18next';
import { todayCv } from '../../utils/date';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658'];

const Reports: React.FC = () => {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<ReportPeriod>('month');
  const [tabValue, setTabValue] = useState(0);
  const [startDate, setStartDate] = useState(() => {
    // Há um mês, em dia de calendário de CV.
    const d = new Date(todayCv() + 'T00:00:00Z');
    d.setUTCMonth(d.getUTCMonth() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(todayCv());

  // Data states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overview, setOverview] = useState<ReportOverview | null>(null);
  const [paymentsOverTime, setPaymentsOverTime] = useState<PaymentOverTimeData[]>([]);
  const [paymentsByEntity, setPaymentsByEntity] = useState<EntityPaymentData[]>([]);
  const [paymentsByMethod, setPaymentsByMethod] = useState<PaymentMethodData[]>([]);
  const [dailyTransactions, setDailyTransactions] = useState<DailyTransactionData[]>([]);

  const formatCurrency = (value: number) => {
    return `${Math.round(value || 0).toLocaleString('pt-PT')}$`;
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all data in parallel
      const [overviewRes, timeRes, entityRes, methodRes, dailyRes] = await Promise.all([
        reportsService.getOverview(period, period === 'custom' ? startDate : undefined, period === 'custom' ? endDate : undefined),
        reportsService.getPaymentsOverTime(period),
        reportsService.getPaymentsByEntity(period),
        reportsService.getPaymentsByMethod(period),
        reportsService.getDailyTransactions(),
      ]);

      if (overviewRes.success) setOverview(overviewRes.data);
      if (timeRes.success) setPaymentsOverTime(timeRes.data);
      if (entityRes.success) setPaymentsByEntity(entityRes.data);
      if (methodRes.success) setPaymentsByMethod(methodRes.data);
      if (dailyRes.success) setDailyTransactions(dailyRes.data);
    } catch (err: any) {
      console.error('Error fetching reports:', err);
      setError(t('common.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [period, startDate, endDate, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = [
    {
      title: t('reports.totalPayments'),
      value: overview?.totalPagamentos?.toLocaleString() || '0',
      change: `${overview?.pagamentosSucesso || 0} ${t('reports.successful')}`,
      icon: <Receipt fontSize="large" color="primary" />,
      color: 'primary.main',
    },
    {
      title: t('reports.totalRevenue'),
      value: formatCurrency(overview?.receitaTotal || 0),
      change: `${overview?.pagamentosPendentes || 0} ${t('reports.pendingAmount')}`,
      icon: <AttachMoney fontSize="large" color="success" />,
      color: 'success.main',
    },
    {
      title: t('reports.activeClients'),
      value: overview?.clientesActivos?.toLocaleString() || '0',
      change: t('reports.registered'),
      icon: <People fontSize="large" color="info" />,
      color: 'info.main',
    },
    {
      title: t('reports.successRate'),
      value: `${overview?.taxaSucesso || 0}%`,
      change: `${overview?.pagamentosFalhados || 0} ${t('reports.failedCount')}`,
      icon: <TrendingUp fontSize="large" color="warning" />,
      color: 'warning.main',
    },
  ];

  if (loading && !overview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          {t('reports.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData} disabled={loading}>
            {loading ? t('common.loading') : t('common.refresh')}
          </Button>
          <Button variant="outlined" startIcon={<DateRange />}>
            {t('reports.advancedFilters')}
          </Button>
          <Button variant="contained" startIcon={<Download />}>
            {t('reports.exportPdf')}
          </Button>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filtros de Período */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label={t('reports.period')}
                value={period}
                onChange={(e) => setPeriod(e.target.value as ReportPeriod)}
              >
                <MenuItem value="today">{t('reports.today')}</MenuItem>
                <MenuItem value="week">{t('reports.thisWeek')}</MenuItem>
                <MenuItem value="month">{t('reports.thisMonth')}</MenuItem>
                <MenuItem value="quarter">{t('reports.thisQuarter')}</MenuItem>
                <MenuItem value="year">{t('reports.thisYear')}</MenuItem>
                <MenuItem value="custom">{t('reports.custom')}</MenuItem>
              </TextField>
            </Grid>
            {period === 'custom' && (
              <>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label={t('reports.startDate')}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label={t('reports.endDate')}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h5" fontWeight={700}>
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.change}
                    </Typography>
                  </Box>
                  <Box sx={{ bgcolor: `${stat.color}15`, p: 1, borderRadius: 2 }}>
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Tabs para diferentes visualizações */}
      <Card sx={{ mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<Timeline />} label={t('reports.tabEvolution')} iconPosition="start" />
          <Tab icon={<BarChart />} label={t('reports.tabByEntity')} iconPosition="start" />
          <Tab icon={<PieChart />} label={t('reports.tabPaymentMethods')} iconPosition="start" />
          <Tab icon={<Assessment />} label={t('reports.tabDailyTransactions')} iconPosition="start" />
        </Tabs>
      </Card>

      {/* Gráficos */}
      {tabValue === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('reports.paymentsEvolution')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            {paymentsOverTime.length > 0 ? (
              <ResponsiveContainer width="100%" height={400} debounce={50}>
                <AreaChart data={paymentsOverTime}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip
                    formatter={(value: any, name: any) => [
                      name === 'payments' ? (value || 0) : formatCurrency(value || 0),
                      name === 'payments' ? t('reports.paymentsLabel') : t('reports.revenueLabel')
                    ]}
                  />
                  <Legend formatter={(value) => value === 'payments' ? t('reports.paymentsLabel') : t('reports.revenueLabel')} />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="payments"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.6}
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="revenue"
                    stroke="#82ca9d"
                    fill="#82ca9d"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {t('reports.noDataPeriod')}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {tabValue === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('reports.paymentsByEntity')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            {paymentsByEntity.length > 0 ? (
              <ResponsiveContainer width="100%" height={400} debounce={50}>
                <RechartsBarChart data={paymentsByEntity}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" orientation="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip
                    formatter={(value: any, name: any) => [
                      name === 'value' ? `${value || 0}%` : formatCurrency(value || 0),
                      name === 'value' ? t('reports.percentage') : t('common.amount')
                    ]}
                  />
                  <Legend formatter={(value) => value === 'value' ? t('reports.percentage') : t('common.amount')} />
                  <Bar yAxisId="left" dataKey="value" fill="#8884d8" />
                  <Bar yAxisId="right" dataKey="amount" fill="#82ca9d" />
                </RechartsBarChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {t('reports.noDataPeriod')}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('reports.paymentMethodDistribution')}
                </Typography>
                <Divider sx={{ mb: 3 }} />
                {paymentsByMethod.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300} debounce={50}>
                    <RechartsPieChart>
                      <Pie
                        data={paymentsByMethod as any}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${((percent || 0) * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {paymentsByMethod.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: any) => `${value || 0}%`} />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {t('common.noData')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('reports.detailsByMethod')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {paymentsByMethod.length > 0 ? (
                  paymentsByMethod.map((method, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {method.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {method.value}% ({method.count} {t('reports.transactionsCount')})
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: '100%',
                          height: 8,
                          bgcolor: 'action.selected',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            width: `${method.value}%`,
                            height: '100%',
                            bgcolor: COLORS[index % COLORS.length],
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatCurrency(method.amount)}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    {t('common.noData')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tabValue === 3 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('reports.dailyTransactions')}
            </Typography>
            <Divider sx={{ mb: 3 }} />
            {dailyTransactions.length > 0 ? (
              <ResponsiveContainer width="100%" height={400} debounce={50}>
                <LineChart data={dailyTransactions}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="day" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip
                    formatter={(value: any, name: any) => [
                      name === 'count' ? (value || 0) : formatCurrency(value || 0),
                      name === 'count' ? t('reports.quantity') : t('common.value')
                    ]}
                  />
                  <Legend formatter={(value) => value === 'count' ? t('reports.quantity') : t('common.value')} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="count"
                    stroke="#8884d8"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="value"
                    stroke="#82ca9d"
                    strokeWidth={2}
                    dot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                {t('reports.noDataWeek')}
              </Typography>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default Reports;
